// ---------------------------------------------------------------------------
// ComplyOne Audit Module — Types, Constants & Helpers
// Matches AUDIT_FRONTEND_API.md strictly
// ---------------------------------------------------------------------------

/**
 * @typedef {'planning' | 'document_gathering' | 'monitoring_and_testing' | 'draft_report' | 'final_report_issued' | 'audit_completed' | 'cancelled'} AuditStatus
 * @typedef {'pending' | 'submitted' | 'sent_back' | 'closed' | 'not_applicable' | 'flagged' | 'possible_finding'} AuditItemStatus
 *
 * @typedef {Object} AuditProgressStage
 * @property {string} key
 * @property {string} label
 * @property {'completed' | 'current' | 'upcoming'} state
 *
 * @typedef {Object} AuditResource
 * @property {number} id
 * @property {string} name
 * @property {string} [code]
 * @property {AuditStatus} status
 * @property {string} status_label
 * @property {AuditProgressStage[]} [progress_stages]
 * @property {string[]} [allowed_transitions]
 * @property {number} workspace_id
 * @property {Array<{id: number, full_name?: string, name?: string, email?: string}>} [clients]
 * @property {Array<{id: number, full_name?: string, name?: string, email?: string}>} [auditors]
 * @property {Array<{id: number, full_name?: string, name?: string, email?: string}>} [analysts]
 * @property {string|null} [period_start]
 * @property {string|null} [period_end]
 * @property {string|null} [due_date]
 * @property {number} [total_items]
 * @property {number} [completed_items]
 * @property {number} [outstanding_items]
 * @property {{total_items: number, completed_items: number, outstanding_items: number, percent: number}} [progress]
 * @property {Object|null} [issuance]
 * @property {boolean} [legal_hold]
 * @property {string|null} [retention_until]
 * @property {Object} [template]
 * @property {boolean} [is_locked]
 * @property {string} [created_at]
 *
 * @typedef {AuditResource & {
 *   description?: string|null,
 *   folder?: Object,
 *   drive_path?: string,
 *   drive_linked?: boolean,
 *   google_connected?: boolean,
 *   request_list?: Array<{id: number, name: string, total_items: number, completed_items: number, progress: number, conclusion?: {id: number, conclusion: string}|null, gaps?: Array<{id: number, text: string, created_by?: Object, created_at?: string}>, items: AuditItemResource[]}>,
 *   activities?: Array<any>
 * }} AuditDetailResource
 *
 * @typedef {Object} AuditItemResource
 * @property {number} id
 * @property {string} [ref]
 * @property {string} title
 * @property {number|null} [assigned_user_id]
 * @property {Object|null} [assigned_user]
 * @property {AuditItemStatus} status
 * @property {string} status_label
 * @property {string} [status_color]
 * @property {string} [client_visible_label]
 * @property {string[]} [allowed_next]
 * @property {boolean} [can_submit_evidence]
 * @property {number} [files_count]
 * @property {string|null} [notes]
 * @property {string|null} [client_notes]
 * @property {boolean} [is_custom]
 * @property {Array<any>} [files]
 *
 * @typedef {Object} IssueReadiness
 * @property {boolean} ready
 * @property {Array<{key: string, label: string, passed: boolean}>} checks
 * @property {Array<{user_id: number, name: string, email: string}>} recipients
 * @property {boolean} is_already_issued
 * @property {string} current_status
 *
 * @typedef {Object} Readiness
 * @property {boolean} all_dispositioned
 * @property {Array<{id: number, ref: string, title: string, status: string, category_name: string}>} pending_items
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const AUDIT_STAGES = [
  { value: 'planning', label: 'Planning' },
  { value: 'document_gathering', label: 'Document gathering' },
  { value: 'monitoring_and_testing', label: 'Monitoring and Testing' },
  { value: 'draft_report', label: 'Draft Report' },
  { value: 'final_report_issued', label: 'Final Report Issued' },
  { value: 'audit_completed', label: 'Audit Completed' },
]

export const AUDIT_STATUSES = [
  ...AUDIT_STAGES,
  { value: 'cancelled', label: 'Cancelled' },
]

export const AUDIT_ITEM_STATUSES = [
  { value: 'pending', label: 'Not started', color: 'slate' },
  { value: 'submitted', label: 'Awaiting review', color: 'amber' },
  { value: 'sent_back', label: 'Sent Back', color: 'orange' },
  { value: 'closed', label: 'Closed', color: 'emerald' },
  { value: 'not_applicable', label: 'Not Applicable', color: 'zinc' },
  { value: 'flagged', label: 'Flagged', color: 'rose' },
  { value: 'possible_finding', label: 'Possible Finding', color: 'amber' },
]

export const ITEM_STATUS_COLORS = {
  pending: 'slate',
  submitted: 'amber',
  sent_back: 'orange',
  closed: 'emerald',
  not_applicable: 'zinc',
  flagged: 'rose',
  possible_finding: 'amber',
}

export const AUDITOR_ROLE_NAME = 'Auditor (Configurable Access)'
export const AUDITEE_ROLE_NAME = 'Auditee'
export const SUPERADMIN_ROLE_NAME = 'super-admin'

export const AUDIT_IN_PROGRESS_STATUSES = new Set([
  'document_gathering',
  'monitoring_and_testing',
  'draft_report',
])

export const AUDIT_SEND_STATUSES = new Set([
  'planning',
  'document_gathering',
])

export const ASSIGNED_TO_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'internal', label: 'Internal' },
]

export function isAuditInProgress(status) {
  return AUDIT_IN_PROGRESS_STATUSES.has(status)
}

export function isAuditPlanning(status) {
  return status === 'planning'
}

export function auditAdutiees(audit) {
  if (!audit) return []
  if (Array.isArray(audit.auditees) && audit.auditees.length) return audit.auditees
  if (audit.auditee) return [audit.auditee]
  return []
}

export function auditAuditors(audit) {
  if (!audit) return []
  if (Array.isArray(audit.auditors) && audit.auditors.length) return audit.auditors
  if (audit.auditor) return [audit.auditor]
  return []
}

/** Ids for the audit's auditees — the API still returns the relation as `clients`/`client`. */
export function auditAuditeeIds(audit) {
  return auditAdutiees(audit).map((u) => u.id).filter(Boolean)
}

export function auditAuditorIds(audit) {
  return auditAuditors(audit).map((u) => u.id).filter(Boolean)
}

/** Users eligible for checklist item assignment — audit auditors plus any already-assigned users. */
export function auditAssignableUsers(audit, items = []) {
  const auditors = auditAuditors(audit)
  const auditees = auditAdutiees(audit)
  const assignedIds = []
  const fallback = [...auditors, ...auditees]

  for (const item of items) {
    const id = item?.assigned_user_id ?? item?.assigned_user?.id
    if (id != null && id !== '') assignedIds.push(id)
    const assignedUser = item?.assigned_user
    if (
      assignedUser?.id != null &&
      !fallback.some((user) => String(user.id) === String(assignedUser.id))
    ) {
      fallback.push(assignedUser)
    }
  }
  // TODO: Remove this once the API is updated to return the auditees in the assigned_users array
  // return withSelectedUsers(auditors, assignedIds, fallback)
  return withSelectedUsers([...auditors, ...auditees], assignedIds, fallback)
}

export function toStatusOptions(statuses = []) {
  if (!Array.isArray(statuses) || !statuses.length) {
    return AUDIT_STATUSES
  }
  return statuses.map((item) => {
    if (typeof item === 'string') {
      return { value: item, label: statusLabel(item) }
    }
    return {
      value: item.value ?? item.status ?? item.key,
      label: item.label ?? item.name ?? statusLabel(item.value ?? item.status ?? item.key),
    }
  })
}

export function allowedAuditTransitions(currentStatus, allowedNext = []) {
  const allowed = Array.isArray(allowedNext) ? allowedNext : []
  return allowed
    .filter((s) => s !== 'document_gathering')
    .map((value) => ({
      value,
      label: `Move to ${statusLabel(value)}`,
    }))
}

// ---------------------------------------------------------------------------
// Helpers from Section 22 & API rules
// ---------------------------------------------------------------------------

/** UUID v4 generator for Idempotency-Key headers. */
export function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** Stable Vue :key for checklist rows — never fall back to bare undefined. */
export function auditItemKey(item, fallback = '') {
  if (item?.id != null && item.id !== '') return `id:${item.id}`
  if (item?._tempKey) return `tmp:${item._tempKey}`
  return fallback ? `row:${fallback}` : 'row:unknown'
}

/** Match the same checklist row across emits (reference, id, or temp key). */
export function matchesAuditItem(row, item) {
  if (!row || !item) return false
  if (row === item) return true

  const rowId = row.id
  const itemId = item.id
  if (rowId != null && rowId !== '' && itemId != null && itemId !== '') {
    return String(rowId) === String(itemId)
  }

  const rowKey = row._tempKey
  const itemKey = item._tempKey
  if (rowKey && itemKey) return rowKey === itemKey

  return false
}

export function isPlanning(audit) {
  return audit?.status === 'planning'
}

/** Issued once it leaves planning, or when an issuance record already exists. */
export function isAuditIssued(audit) {
  if (!audit) return false
  return Boolean(audit.issuance) || !isPlanning(audit)
}

/**
 * Primary auditor for an engagement.
 * Prefer an `is_primary` flag on the auditors list, then the singular `auditor`
 * relation, then list order (create/update payloads put the primary first).
 */
export function primaryAuditor(audit) {
  if (!audit) return null

  const auditors = auditAuditors(audit)
  const flagged = auditors.find((user) => user.is_primary || user.pivot?.is_primary)
  if (flagged) return flagged

  if (audit.auditor?.id != null) return audit.auditor
  return auditors[0] ?? null
}

export function primaryAuditorId(audit) {
  const id = primaryAuditor(audit)?.id
  return id != null && id !== '' ? id : null
}

export function isPrimaryAuditor(audit, userId) {
  if (userId == null || userId === '') return false
  const primaryId = primaryAuditorId(audit)
  if (primaryId == null) return false
  return String(primaryId) === String(userId)
}

export function isLocked(audit) {
  return Boolean(audit?.is_locked)
}

export function canIssue(audit, readiness) {
  return (
    isPlanning(audit) &&
    Boolean(readiness?.ready) &&
    !readiness?.is_already_issued &&
    !isLocked(audit)
  )
}

export function canUpload(audit, item = null) {
  if (!audit) return false
  const stageAllowed = ['document_gathering', 'monitoring_and_testing'].includes(audit.status)
  if (!stageAllowed || isLocked(audit)) return false
  return item ? Boolean(item.can_submit_evidence) : true
}

export function canReview(audit) {
  if (!audit) return false
  return ['document_gathering', 'monitoring_and_testing'].includes(audit.status) && !isLocked(audit)
}

export function canConclude(readiness) {
  return Boolean(readiness?.all_dispositioned)
}

export function canStartReport(readiness, categories = []) {
  if (!canConclude(readiness)) return false
  if (!categories || !categories.length) return false
  return categories.every((c) => Boolean(c.conclusion && (typeof c.conclusion === 'string' ? c.conclusion.trim() : c.conclusion.conclusion)))
}

export function canAttest(audit) {
  return audit?.status === 'draft_report' && !isLocked(audit)
}

/** Allowed transitions from the API object only. Never inject document_gathering. */
export function transitionOptions(audit) {
  return Array.isArray(audit?.allowed_transitions) ? audit.allowed_transitions : []
}

export function isItemComplete(status) {
  return ['closed', 'not_applicable', 'flagged', 'possible_finding'].includes(status)
}

export function incompleteAuditItems(items = []) {
  return (items || []).filter((item) => !isItemComplete(item?.status))
}

export function isItemOpen(status) {
  return ['pending', 'submitted', 'sent_back'].includes(status)
}

export function isReturnedItem(item) {
  return ['sentback', 'sent_back'].includes(item?.status)
}

export function isAttentionItem(item) {
  return ['sentback', 'sent_back', 'flagged', 'possible_finding'].includes(item?.status)
}

export function isNeedsActionItem(item) {
  return ['pending', 'flagged'].includes(item?.status)
}

export function isClientOpenItem(item) {
  return ['pending', 'sentback', 'sent_back', 'flagged'].includes(item?.status)
}

export function isClientAnsweredItem(item) {
  return ['submitted', 'closed', 'not_applicable'].includes(item?.status)
}

export function isResolvedItem(item) {
  return ['closed', 'not_applicable'].includes(item?.status)
}

export function itemFilesCount(item) {
  if (Array.isArray(item?.files)) return item.files.length
  if (item?.files_count != null) return Number(item.files_count) || 0
  return item?.files ? 1 : 0
}

export function flattenAuditItems(categories = []) {
  const out = []
  for (const category of categories) {
    for (const item of category.items || []) {
      out.push({ item, category })
    }
  }
  return out
}

export function auditEvidenceStats(items = []) {
  const required = items.filter((item) => item.status !== 'not_applicable')
  const uploaded = required.filter((item) => itemFilesCount(item) > 0)
  const missing = required.filter(
    (item) => itemFilesCount(item) === 0 && !isResolvedItem(item),
  )
  const completed = items.filter((item) => isResolvedItem(item))
  return {
    total: items.length,
    required: required.length,
    uploaded: uploaded.length,
    missing: missing.length,
    completed: completed.length,
    percent: items.length ? Math.round((completed.length / items.length) * 100) : 0,
  }
}

// ---------------------------------------------------------------------------
// Stage Resolution
// ---------------------------------------------------------------------------

export function resolveAuditStageIndex(audit) {
  if (!audit || audit.status === 'cancelled') return -1

  // `audit.status` is the source of truth. Prefer it over `state: 'current'` so a
  // stale API flag on an earlier stage cannot keep a passed step looking active.
  const byStatus = AUDIT_STAGES.findIndex((s) => s.value === audit.status)
  if (byStatus >= 0) return byStatus

  if (Array.isArray(audit.progress_stages) && audit.progress_stages.length) {
    const fromKey = audit.progress_stages.findIndex((s) => s.key === audit.status)
    if (fromKey >= 0) return fromKey
    const fromState = audit.progress_stages.findIndex((s) => s.state === 'current')
    if (fromState >= 0) return fromState
  }

  return 0
}

export function resolveAuditStage(audit) {
  const index = resolveAuditStageIndex(audit)
  return index >= 0 ? AUDIT_STAGES[index] : null
}

// ---------------------------------------------------------------------------
// Status / Label Helpers
// ---------------------------------------------------------------------------

export function formatAuditLabel(value) {
  if (value == null || value === '') return '—'
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function statusLabel(value, options = AUDIT_STATUSES) {
  return options.find((option) => option.value === value)?.label || formatAuditLabel(value)
}

export function itemStatusLabel(value, item = {}) {
  if (item?.status === value && item?.status_label) return item.status_label
  const status = value === 'sentback' ? 'sent_back' : value
  return AUDIT_ITEM_STATUSES.find((opt) => opt.value === status)?.label || formatAuditLabel(value)
}

/** Group incomplete items by canonical status so dialogs/overviews share one count. */
export function countIncompleteItemsByStatus(items = []) {
  const counts = new Map()
  for (const item of incompleteAuditItems(items)) {
    const status = item?.status === 'sentback' ? 'sent_back' : item?.status
    counts.set(status, (counts.get(status) || 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => ({
      status,
      count,
      label: itemStatusLabel(status),
    }))
}

export function itemStatusColor(value, item = {}) {
  if (item?.status === value && item?.status_color) return item.status_color
  return ITEM_STATUS_COLORS[value] || 'slate'
}

/** Badge variant for an audit-level status, matching `StatusBadge` colors without the spinner. */
export function auditStatusBadgeVariant(status) {
  switch (String(status || '').toLowerCase()) {
    case 'planning':
    case 'in_progress':
      return 'pending'
    case 'document_gathering':
    case 'monitoring_and_testing':
      return 'neutral'
    case 'draft_report':
    case 'under_review':
    case 'submitted':
      return 'warning'
    case 'final_report_issued':
    case 'audit_completed':
    case 'completed':
      return 'success'
    case 'cancelled':
      return 'danger'
    default:
      return 'primary'
  }
}

/** Badge variant for a checklist item. `closed` is complete here, not a danger state. */
export function itemBadgeVariant(status) {
  switch (String(status || '').toLowerCase()) {
    case 'closed':
      return 'success'
    case 'submitted':
      return 'info'
    case 'sentback':
    case 'sent_back':
      return 'danger'
    case 'flagged':
    case 'possible_finding':
      return 'warning'
    case 'not_applicable':
    case 'waived':
      return 'neutral'
    default:
      return 'secondary'
  }
}

/** Copy for close / flag / send-back confirmations (required note). */
export const ITEM_REVIEW_ACTION_COPY = {
  closed: {
    title: 'Close this item?',
    description: 'This closes the item and marks it complete. The requester will see it as resolved.',
    label: 'Why are you closing this item?',
    placeholder: 'e.g. Evidence is complete and matches the request.',
    confirmLabel: 'Close item',
    confirmVariant: 'default',
    event: 'close',
  },
  flagged: {
    title: 'Flag this item?',
    description: 'This flags the item as a possible finding for follow-up.',
    label: 'Why are you flagging this item?',
    placeholder: 'e.g. Possible finding — the policy does not cover this control.',
    confirmLabel: 'Flag item',
    confirmVariant: 'default',
    event: 'flag',
  },
  sent_back: {
    title: 'Send item back to client',
    description: 'Your comment is required — the client will see exactly what you write here.',
    label: 'What do you need from the client?',
    placeholder: 'e.g. The attached letter doesn\'t include a registration number — can you resend the original confirmation?',
    confirmLabel: 'Send back',
    confirmVariant: 'destructive',
    event: 'sendback',
  },
}

export function itemReviewActionCopy(action) {
  if (!action) return null
  const key = action === 'sentback' ? 'sent_back' : action
  return ITEM_REVIEW_ACTION_COPY[key] ?? null
}

/** Close / flag / send-back — auditors may take these from any item status. */
export const ITEM_REVIEW_ACTIONS = ['closed', 'flagged', 'sent_back']

/** True unless the item is already in that review status (a no-op). */
export function auditorCanReviewAction(item, action) {
  if (!ITEM_REVIEW_ACTIONS.includes(action)) return false
  return item?.status !== action
}

export function auditorReviewActions(item) {
  return ITEM_REVIEW_ACTIONS.filter((action) => auditorCanReviewAction(item, action))
}

// ---------------------------------------------------------------------------
// Unwrapping helpers
// ---------------------------------------------------------------------------

export function unwrapResource(data, keys = []) {
  if (data == null) return data
  for (const key of keys) {
    if (data[key] != null) return data[key]
  }
  return data
}

export function unwrapCollection(data, keys = []) {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key]
  }
  if (Array.isArray(data)) return data
  return []
}

/** Normalizes an audits list payload (`GET .../audits`) for the list page and the store. */
export function normalizeAuditList(data) {
  return {
    audits: unwrapCollection(data, ['audits', 'data']),
    statuses: data?.statuses ?? data?.status ?? [],
    pagination: data?.pagination ?? {},
    metrics: Array.isArray(data?.metrics) ? data.metrics : [],
  }
}

// ---------------------------------------------------------------------------
// User Display & Pickers
// ---------------------------------------------------------------------------

export function userDisplayName(user) {
  if (!user) return '—'
  return (
    user.full_name ||
    user.name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.email ||
    '—'
  )
}

export function usersDisplayNames(users = []) {
  if (!Array.isArray(users) || !users.length) return '—'
  return users.map(userDisplayName).filter((name) => name && name !== '—').join(', ') || '—'
}

/** First token of a role name: "Auditor (Configurable Access)" → "auditor". */
function roleNameToken(name) {
  return String(name || '').trim().toLowerCase().split(/[\s(/]/)[0]
}

/**
 * True when two role labels refer to the same workspace role.
 * Exact match first, then first-token so "Auditor" matches
 * "Auditor (Configurable Access)" without colliding with "Auditee".
 */
export function roleNamesMatch(actual, expected) {
  const a = String(actual || '').trim().toLowerCase()
  const b = String(expected || '').trim().toLowerCase()
  if (!a || !b) return false
  if (a === b) return true
  const tokenA = roleNameToken(a)
  const tokenB = roleNameToken(b)
  return Boolean(tokenA && tokenB && tokenA === tokenB)
}

export function findRoleIdByName(roles = [], roleName = AUDITOR_ROLE_NAME) {
  const needle = String(roleName || '').trim()
  if (!needle) return null
  const list = Array.isArray(roles) ? roles : []
  const role = list.find((item) => roleNamesMatch(item?.name, needle))
  return role?.id ?? null
}

/**
 * Flattens a user's `role` (string, or {name}/{id} object) and `roles` array
 * into role name strings. Shared beyond audits — e.g. authStore role checks —
 * since user objects come back in both shapes depending on the endpoint.
 */
export function userRoleNames(user) {
  const names = []
  const role = user?.role
  if (typeof role === 'string') names.push(role)
  else if (role?.name) names.push(role.name)
  if (Array.isArray(user?.roles)) {
    for (const entry of user.roles) {
      if (typeof entry === 'string') names.push(entry)
      else if (entry?.name) names.push(entry.name)
    }
  }
  return names.map(String)
}

/** True when the user has exactly one role and it is Customer. */
export function isCustomerOnlyUser(user) {
  const unique = [
    ...new Set(
      userRoleNames(user)
        .map((name) => String(name).trim().toLowerCase())
        .filter(Boolean),
    ),
  ]
  return unique.length === 1 && roleNamesMatch(unique[0], 'customer')
}

export function usersWithRole(users = [], roleName) {
  const roles = Array.isArray(roleName) ? roleName : [roleName]

  const roleSet = new Set(
    roles
      .map((role) => String(role ?? '').trim().toLowerCase())
      .filter(Boolean),
  )

  if (!roleSet.size) return users

  return users.filter((user) =>
    userRoleNames(user).some((name) =>
      [...roleSet].some((expected) => roleNamesMatch(name, expected)),
    ),
  )
}

export function withSelectedUsers(list = [], selectedIds = [], fallbackPool = []) {
  const selected = new Set((selectedIds || []).map(String))
  if (!selected.size) return list
  const byId = new Map(list.map((user) => [String(user.id), user]))
  for (const user of fallbackPool) {
    if (selected.has(String(user.id)) && !byId.has(String(user.id))) {
      byId.set(String(user.id), user)
    }
  }
  return [...byId.values()]
}

export function toUserOptions(users = []) {
  return users
    .map((user) => ({
      label: userDisplayName(user),
      value: String(user.id),
      email: user.email,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

// ---------------------------------------------------------------------------
// Template Helpers
// ---------------------------------------------------------------------------

export function emptyTemplateCategory(name = '') {
  return {
    name,
    sort_order: 0,
    items: [],
  }
}

export function emptyTemplateItem(title = '') {
  return {
    title,
    sort_order: 0,
  }
}

export function normalizeTemplateCategories(categories = []) {
  return categories.map((category, categoryIndex) => ({
    id: category.id,
    name: category.name,
    sort_order: category.sort_order ?? categoryIndex,
    items: (category.items || []).map((item, itemIndex) => ({
      id: item.id,
      title: item.title,
      sort_order: item.sort_order ?? itemIndex,
    })),
  }))
}
